export const CAMPAIGN_KINDS = [
  'call',
  'sales_email',
  'marketing_email',
  'event',
  'paid_media',
  'other',
] as const

export type CampaignKind = (typeof CAMPAIGN_KINDS)[number]

/**
 * Two facts this module deliberately refuses to collapse.
 *
 * `CampaignChannelEvidence` answers "what does the CRM currently record?"
 * `CampaignExecutionClearance` answers "is this contact cleared for this
 * campaign action?"
 *
 * A phone row is evidence, not do-not-call permission or local-time clearance.
 * An email address is evidence, not consent, suppression clearance,
 * deliverability, or authority to send. Nothing is `verified` unless the data
 * in front of us genuinely proves it — and in this read-only beta, nothing
 * does.
 */
export type CampaignChannelEvidence = 'present' | 'missing' | 'unverified'
export type CampaignExecutionClearance =
  | 'verified'
  | 'review_required'
  | 'blocked'

/** The single roll-up a row/metric can be bucketed into. */
export type CampaignPreflightOutcome =
  | 'cleared'
  | 'review_required'
  | 'missing_evidence'
  | 'blocked'

export const CAMPAIGN_SEARCH_TERM_LIMIT = 8
export const CAMPAIGN_SEARCH_CHARACTER_LIMIT = 120

export interface CampaignAudienceContact {
  id: string
  first_name: string
  last_name: string
  email: string | null
  is_active: boolean
  phone_count: number
}

export interface CampaignAudienceRow extends CampaignAudienceContact {
  account_id: string
  account: {
    id: string
    name: string
  } | null
}

export interface CampaignContactPreflight {
  evidence: CampaignChannelEvidence
  evidenceReason: string
  clearance: CampaignExecutionClearance
  clearanceReason: string
  outcome: CampaignPreflightOutcome
}

export interface CampaignAudienceMetrics {
  /** Rows actually assessed — never the whole-audience total. */
  assessed: number
  evidencePresent: number
  evidenceMissing: number
  evidenceUnverified: number
  clearanceVerified: number
  clearanceReview: number
  clearanceBlocked: number
}

/**
 * What the CRM would have to record before a contact could count as evidence
 * for this campaign kind. `none` means this beta cannot honestly say which
 * fields matter, so evidence stays `unverified` rather than being invented.
 */
type CampaignChannelRequirement = 'phone' | 'email' | 'email_or_phone' | 'none'

export interface CampaignKindRule {
  label: string
  description: string
  channel: CampaignChannelRequirement
  /** Metric label for rows that do hold the required evidence. */
  evidencePresentLabel: string
  /** Metric label for rows that do not. */
  evidenceMissingLabel: string
  /** Metric label used when evidence cannot be assessed at all. */
  evidenceUnverifiedLabel: string
  /**
   * Clearance for an active contact of this kind. There is no `verified`
   * branch anywhere in this beta because no stored data proves permission.
   */
  clearance: {
    state: Exclude<CampaignExecutionClearance, 'verified'>
    reason: string
  }
  /** Plain-language facts this CRM cannot currently establish. */
  unknown: readonly string[]
  /** Plain-language reasons a launch could not proceed even with the data. */
  blockers: readonly string[]
}

export const CAMPAIGN_KIND_RULES: Record<CampaignKind, CampaignKindRule> = {
  call: {
    label: 'Call campaign',
    description:
      'Plan a manual call list. A phone record shows only that a number was typed in somewhere.',
    channel: 'phone',
    evidencePresentLabel: 'Phone on file',
    evidenceMissingLabel: 'No phone on file',
    evidenceUnverifiedLabel: 'Phone evidence not assessed',
    clearance: {
      state: 'review_required',
      reason:
        'Do-not-call status, time zone and legal calling hours, and who owns the number are not recorded here.',
    },
    unknown: [
      'Whether each stored number is real, current, and dialable',
      'Whether the contact has asked not to be called',
      'The contact’s time zone, and which hours you may legally call',
      'Whether the number belongs to the person or to their employer',
    ],
    blockers: [
      'There is no call list, dialer, or call history to work from yet',
      'Do-not-call requests and calling-hour rules are not stored anywhere yet',
    ],
  },
  sales_email: {
    label: 'Sales email',
    description:
      'Plan one-to-one outreach. A recorded address shows only that someone typed an address in.',
    channel: 'email',
    evidencePresentLabel: 'Email on file',
    evidenceMissingLabel: 'No email on file',
    evidenceUnverifiedLabel: 'Email evidence not assessed',
    clearance: {
      state: 'review_required',
      reason:
        'No mailbox is connected, and bounces, replies, and who is allowed to email this contact are not recorded here.',
    },
    unknown: [
      'Whether a mailbox is connected to send from',
      'Whether earlier messages to this address bounced or were rejected',
      'Whether the contact already replied or asked you to stop',
      'Who on your team is allowed to email this contact',
    ],
    blockers: [
      'No mailbox is connected, so nothing can be sent',
      'Replies, bounces, and opt-outs are not tracked yet',
    ],
  },
  marketing_email: {
    label: 'Marketing email',
    description:
      'Assess email coverage. An address is never permission to send marketing.',
    channel: 'email',
    evidencePresentLabel: 'Email on file',
    evidenceMissingLabel: 'No email on file',
    evidenceUnverifiedLabel: 'Email evidence not assessed',
    clearance: {
      state: 'blocked',
      reason:
        'Marketing email needs recorded consent, an unsubscribe link, a suppression list, and frequency limits. None of those exist here yet.',
    },
    unknown: [
      'Whether the contact agreed to receive marketing email, and for what',
      'Whether they previously unsubscribed or reported a message as spam',
      'How often they have already been emailed',
      'Whether your sending domain is set up to send in bulk',
    ],
    blockers: [
      'Marketing email is blocked: there is no consent record for any contact',
      'There is no unsubscribe link, preference page, or suppression list',
      'There are no frequency limits, and no bounce or complaint handling',
      'No bulk email provider is connected',
    ],
  },
  event: {
    label: 'Event',
    description:
      'Plan invitations for contacts you hold at least one direct channel for.',
    channel: 'email_or_phone',
    evidencePresentLabel: 'Email or phone on file',
    evidenceMissingLabel: 'No email or phone on file',
    evidenceUnverifiedLabel: 'Contact evidence not assessed',
    clearance: {
      state: 'review_required',
      reason:
        'Whether the contact wants invitations, and how one would actually reach them, are not recorded here.',
    },
    unknown: [
      'Whether the contact wants event invitations from you',
      'How the invitation would actually be delivered',
      'The event itself — dates, capacity, and how people register',
    ],
    blockers: [
      'There is no event record, invitation, or registration list yet',
      'No invitation can be delivered until a sending channel is connected',
    ],
  },
  paid_media: {
    label: 'Paid media',
    description:
      'Review a CRM audience before any future, separately approved ad-platform export.',
    channel: 'none',
    evidencePresentLabel: 'Matchable records',
    evidenceMissingLabel: 'Unmatchable records',
    evidenceUnverifiedLabel: 'Match evidence not assessed',
    clearance: {
      state: 'review_required',
      reason:
        'No ad platform is connected, so nothing here can be confirmed as a usable or permitted audience.',
    },
    unknown: [
      'Which stored fields an ad platform could actually match on',
      'Whether these contacts agreed to ad targeting',
      'Whether exporting this audience is permitted at all',
    ],
    blockers: [
      'No ad platform is connected',
      'Exporting an audience is not built, and is not permitted from this screen',
    ],
  },
  other: {
    label: 'Other',
    description:
      'Explore an audience for a campaign whose channel has not been chosen yet.',
    channel: 'none',
    evidencePresentLabel: 'Records with a channel',
    evidenceMissingLabel: 'Records without a channel',
    evidenceUnverifiedLabel: 'Channel evidence not assessed',
    clearance: {
      state: 'review_required',
      reason:
        'No channel has been chosen, so there is nothing yet to clear these contacts against.',
    },
    unknown: [
      'Which channel this campaign would actually use',
      'What permission that channel would require',
    ],
    blockers: [
      'Choose a specific campaign type to see its real launch requirements',
    ],
  },
}

export const CAMPAIGN_BETA_REASON =
  'This is a read-only planning beta. Nothing on this screen is saved, sent, dialed, or exported.'

/**
 * A status badge's label and its two DESIGN.md §5 colours.
 *
 * Deliberately **not** a Tailwind class string. Both pre-existing status badges
 * in this repo are shaped exactly this way — `STAGE_CONFIG`
 * (`src/lib/pipeline.ts:48-53`, consumed by `pipeline/stage-badge.tsx`) and
 * `TYPE_CONFIG` (`accounts/account-badge.tsx:7-11`) — and each is applied
 * through an inline `style` on a `variant="secondary"` `<Badge>`.
 *
 * That convention is load-bearing, not cosmetic. `<Badge>`'s default variant
 * carries `hover:bg-primary/80`, and `tailwind-merge` keys conflicts on
 * **(modifier set, class group)**: a `className` of
 * `bg-[color:var(--status-quoted-bg)]` displaces the unmodified `bg-primary`
 * but leaves the `hover:` one intact, because it sits in a different key. Since
 * `applyAccentTokens` sets `--primary` from the tenant's exact unclamped brand
 * accent (`src/lib/theme.ts:635` writes `--primary` from `ramp.accent` as a
 * rounded HSL triplet; DESIGN.md §6 rule 3 defines that accent as the tenant's
 * exact unclamped hex), a class-styled status badge **replaces its own status tint
 * with the raw brand colour on hover** — same CSS property, so the tint is
 * displaced rather than layered under, and the 80% alpha composites over the row
 * or card surface behind the badge. DESIGN.md §6 line 102 forbids accent on badges
 * and status outright, and §11 makes tenant accent conveying status an automatic
 * Originality FAIL. It was also a genuine WCAG 1.4.3 failure: for most of the
 * graded swap accents §6 itself uses, the hovered label fell below the AA floor
 * and in several accent/mode combinations below 3:1. No ratio is quoted because
 * **no gate computes one** — not because none is computable; `contrastRatio` in
 * `src/lib/theme.ts` computes exactly this, and an earlier revision of this
 * comment wrongly claimed nothing in the repo could.
 *
 * An inline `style` outranks every class rule, `:hover` included, so expressing
 * these as data makes the leak unrepresentable rather than merely fixed.
 */
export interface CampaignBadge {
  label: string
  fg: string
  bg: string
}

/**
 * Channel evidence is informational, so it takes the informational (`quoted`)
 * and neutral (`expired`) pairs. It is never success-coloured: holding a phone
 * number is not permission to ring it.
 */
export const CAMPAIGN_EVIDENCE_BADGE: Record<
  CampaignChannelEvidence,
  CampaignBadge
> = {
  present: {
    label: 'On file',
    fg: 'var(--status-quoted-fg)',
    bg: 'var(--status-quoted-bg)',
  },
  missing: {
    label: 'Not on file',
    fg: 'var(--status-expired-fg)',
    bg: 'var(--status-expired-bg)',
  },
  unverified: {
    label: 'Not assessed',
    fg: 'var(--status-expired-fg)',
    bg: 'var(--status-expired-bg)',
  },
}

/**
 * No green is reachable on this screen. Green reads as "cleared to act", and
 * this beta cannot prove clearance for any contact on any channel —
 * `CampaignKindRule.clearance` excludes `verified` at the type level, so the
 * `verified` entry below is a placeholder kept only to make the map exhaustive
 * for C2. Clearance is caution (`on_hold`) or refusal (`lost`).
 */
export const CAMPAIGN_CLEARANCE_BADGE: Record<
  CampaignExecutionClearance,
  CampaignBadge
> = {
  verified: {
    label: 'Cleared',
    fg: 'var(--status-won-fg)',
    bg: 'var(--status-won-bg)',
  },
  review_required: {
    label: 'Needs review',
    fg: 'var(--status-on-hold-fg)',
    bg: 'var(--status-on-hold-bg)',
  },
  blocked: {
    label: 'Blocked',
    fg: 'var(--status-lost-fg)',
    bg: 'var(--status-lost-bg)',
  },
}

/** The launch-readiness verdict. This beta has exactly one, and it is a refusal. */
export const CAMPAIGN_LAUNCH_BADGE: CampaignBadge = {
  label: 'Cannot launch yet',
  fg: 'var(--status-lost-fg)',
  bg: 'var(--status-lost-bg)',
}

function hasText(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0
}

function channelEvidence(
  channel: CampaignChannelRequirement,
  contact: CampaignAudienceContact,
): { evidence: CampaignChannelEvidence; reason: string } {
  const emailPresent = hasText(contact.email)
  const phonePresent = contact.phone_count > 0

  switch (channel) {
    case 'phone':
      return phonePresent
        ? {
            evidence: 'present',
            reason: 'Phone record on file — the number itself is not checked',
          }
        : { evidence: 'missing', reason: 'No phone record' }
    case 'email':
      return emailPresent
        ? {
            evidence: 'present',
            reason: 'Email address on file — the address itself is not checked',
          }
        : { evidence: 'missing', reason: 'No email address' }
    case 'email_or_phone':
      if (emailPresent || phonePresent) {
        return {
          evidence: 'present',
          reason: emailPresent
            ? 'Email address on file'
            : 'Phone record on file',
        }
      }
      return { evidence: 'missing', reason: 'No email address or phone record' }
    case 'none':
      return {
        evidence: 'unverified',
        reason:
          'This campaign type has no agreed contact requirement yet, so nothing here counts for or against it',
      }
  }
}

function preflightOutcome(
  evidence: CampaignChannelEvidence,
  clearance: CampaignExecutionClearance,
): CampaignPreflightOutcome {
  if (clearance === 'blocked') return 'blocked'
  if (evidence === 'missing') return 'missing_evidence'
  if (clearance === 'review_required' || evidence === 'unverified') {
    return 'review_required'
  }
  return 'cleared'
}

export function assessCampaignContact(
  kind: CampaignKind,
  contact: CampaignAudienceContact,
): CampaignContactPreflight {
  const rule = CAMPAIGN_KIND_RULES[kind]
  // Evidence is reported honestly even for a contact that is blocked for an
  // unrelated reason: the two axes never overwrite one another.
  const { evidence, reason: evidenceReason } = channelEvidence(
    rule.channel,
    contact,
  )

  const { state: clearance, reason: clearanceReason } = contact.is_active
    ? rule.clearance
    : {
        state: 'blocked' as const,
        reason:
          'This contact is marked inactive, so no campaign may act on them.',
      }

  return {
    evidence,
    evidenceReason,
    clearance,
    clearanceReason,
    outcome: preflightOutcome(evidence, clearance),
  }
}

export function measureCampaignAudience(
  kind: CampaignKind,
  contacts: readonly CampaignAudienceContact[],
): CampaignAudienceMetrics {
  const metrics: CampaignAudienceMetrics = {
    assessed: contacts.length,
    evidencePresent: 0,
    evidenceMissing: 0,
    evidenceUnverified: 0,
    clearanceVerified: 0,
    clearanceReview: 0,
    clearanceBlocked: 0,
  }

  for (const contact of contacts) {
    const { evidence, clearance } = assessCampaignContact(kind, contact)
    if (evidence === 'present') metrics.evidencePresent += 1
    else if (evidence === 'missing') metrics.evidenceMissing += 1
    else metrics.evidenceUnverified += 1

    if (clearance === 'verified') metrics.clearanceVerified += 1
    else if (clearance === 'review_required') metrics.clearanceReview += 1
    else metrics.clearanceBlocked += 1
  }

  return metrics
}

/**
 * Fail-closed guard: aggregate metrics must never contradict the exact
 * server-side audience total or the bounded preview window. Returns a
 * diagnostic message when they do, so the caller can refuse to render numbers
 * it cannot stand behind.
 */
export function campaignMetricsInconsistency(
  metrics: CampaignAudienceMetrics,
  total: number,
  limit: number,
): string | null {
  if (!Number.isSafeInteger(total) || total < 0) {
    return 'The accessible total is not a whole number.'
  }
  if (!Number.isSafeInteger(limit) || limit <= 0) {
    return 'The preview limit is not a positive whole number.'
  }
  const buckets = [
    metrics.assessed,
    metrics.evidencePresent,
    metrics.evidenceMissing,
    metrics.evidenceUnverified,
    metrics.clearanceVerified,
    metrics.clearanceReview,
    metrics.clearanceBlocked,
  ]
  // Checked before the sums, because offsetting negatives would otherwise add
  // up correctly and pass.
  if (buckets.some((value) => !Number.isSafeInteger(value) || value < 0)) {
    return 'A count came back negative or fractional.'
  }
  if (metrics.assessed > limit) {
    return 'More rows were assessed than the preview limit allows.'
  }
  if (metrics.assessed > total) {
    return 'More rows were assessed than the accessible total reports.'
  }
  const expected = Math.min(total, limit)
  if (metrics.assessed !== expected) {
    return total <= limit
      ? 'The assessed rows do not cover the whole accessible audience.'
      : 'The bounded preview did not fill its window.'
  }
  if (
    metrics.evidencePresent +
      metrics.evidenceMissing +
      metrics.evidenceUnverified !==
    metrics.assessed
  ) {
    return 'The channel evidence counts do not add up to the assessed rows.'
  }
  if (
    metrics.clearanceVerified +
      metrics.clearanceReview +
      metrics.clearanceBlocked !==
    metrics.assessed
  ) {
    return 'The clearance counts do not add up to the assessed rows.'
  }
  return null
}

/**
 * Why the audience read is, or is not, allowed to put numbers on screen.
 *
 * Exactly one of these is true at any moment, and only `ready` permits a
 * numeric fact. The previous revision asked the inverse question — a boolean
 * built from `!busy && !searchError && !isError && !metricsProblem` — which
 * fails **open**: every one of those is false when a read has not happened at
 * all, so "nothing went wrong" was mistaken for "the answer is zero".
 *
 * The concrete escape was TanStack Query's paused state. With the default
 * `networkMode: 'online'` an offline query gets `status: 'pending'` and
 * `fetchStatus: 'paused'` — never `'fetching'` — so `isLoading`
 * (`isPending && isFetching`) is **false**, `isError` is false, and `data` is
 * `undefined`, which also makes the metrics backstop `null` because it is
 * gated on data. All four negatives passed and the screen rendered
 * "Accessible contacts 0" for a read that never left the browser.
 *
 * So the question is asked positively instead: every input that must hold is
 * named, and anything short of all of them is classified rather than assumed
 * benign.
 */
export type CampaignAudienceReadState =
  | 'invalid_search'
  | 'error'
  | 'paused'
  | 'loading'
  | 'unread'
  | 'inconsistent'
  | 'ready'

export interface CampaignAudienceReadInputs {
  /** TanStack Query's `status`. */
  status: 'pending' | 'success' | 'error'
  /** TanStack Query's `fetchStatus`. `'paused'` means the fetch never ran. */
  fetchStatus: 'fetching' | 'paused' | 'idle'
  /** `data !== undefined` — decoded payload for the *current* key. */
  hasData: boolean
  /** The typed search has reached the query that produced `hasData`. */
  searchSettled: boolean
  /** The search definition is accepted, so a query was allowed to run. */
  searchValid: boolean
  /** `campaignMetricsInconsistency(...) === null`. */
  metricsConsistent: boolean
}

/**
 * Total and deterministic: every combination of inputs lands on exactly one
 * state, and `ready` is reachable only when all six conditions hold at once.
 *
 * Order is load-bearing:
 * 1. an invalid search disables the query, so nothing downstream is meaningful;
 * 2. a failed read is a failure regardless of what else is true;
 * 3. `paused` outranks `loading` — a paused fetch is stalled, not in flight,
 *    and it outranks `unread` so the screen can say *why* nothing was read;
 * 4. an unsettled search is loading even when stale data for another key sits
 *    in the cache;
 * 5. no data left over is `unread` rather than "zero";
 * 6. only then may the decoded invariants be trusted enough to be checked.
 *
 * Note that `paused` refuses even when `hasData` is true. Stale rows from a
 * cache entry the server has not confirmed are exactly the "counts the server
 * just refused to confirm" case, so a paused refetch withdraws them.
 */
export function resolveCampaignAudienceReadState(
  inputs: CampaignAudienceReadInputs,
): CampaignAudienceReadState {
  if (!inputs.searchValid) return 'invalid_search'
  if (inputs.status === 'error') return 'error'
  if (inputs.fetchStatus === 'paused') return 'paused'
  if (!inputs.searchSettled) return 'loading'
  if (inputs.status === 'pending') {
    return inputs.fetchStatus === 'fetching' ? 'loading' : 'unread'
  }
  if (!inputs.hasData) return 'unread'
  if (!inputs.metricsConsistent) return 'inconsistent'
  return 'ready'
}

/**
 * The single gate every numeric fact on the screen passes through. Written as
 * its own function so no call site can re-derive it as a negation and
 * reintroduce the fail-open bug.
 */
export function campaignAudienceShowsNumbers(
  state: CampaignAudienceReadState,
): boolean {
  return state === 'ready'
}

/**
 * What to tell the user instead of a number. `ready` has no message — asking
 * for one is a caller bug, so it returns `null` and the caller must branch.
 */
export function campaignAudienceRefusal(
  state: CampaignAudienceReadState,
): string | null {
  switch (state) {
    case 'invalid_search':
      return 'Fix the search definition before assessing contacts.'
    case 'error':
      return 'No contacts were assessed, because this audience could not be read.'
    case 'paused':
      return 'No contacts were assessed, because this device is offline.'
    case 'loading':
      return 'Loading accessible contacts…'
    case 'unread':
      return 'No contacts were assessed, because this audience has not been read yet.'
    case 'inconsistent':
      return 'No contacts were assessed, because these audience numbers do not reconcile.'
    case 'ready':
      return null
  }
}

export interface CampaignLaunchReadiness {
  /** This beta has exactly one state. C2 may add others. */
  state: 'cannot_launch'
  headline: string
  /** True when the assessed rows are only part of the accessible audience. */
  partial: boolean
  known: string[]
  unknown: readonly string[]
  blockers: readonly string[]
  betaReason: string
}

function count(value: number): string {
  return value.toLocaleString()
}

function launchHeadline(rule: CampaignKindRule): string {
  return rule.clearance.state === 'blocked'
    ? `${rule.label} is blocked. ${rule.clearance.reason}`
    : `${rule.label} cannot launch yet. ${rule.clearance.reason}`
}

export function campaignLaunchReadiness(
  kind: CampaignKind,
  metrics: CampaignAudienceMetrics,
  total: number,
): CampaignLaunchReadiness {
  const rule = CAMPAIGN_KIND_RULES[kind]
  const partial = metrics.assessed < total
  const assessed = count(metrics.assessed)
  const scope = partial
    ? `the first ${assessed} of ${count(total)}`
    : `all ${count(total)}`

  const known: string[] = [
    `These filters reach ${count(total)} ${total === 1 ? 'contact' : 'contacts'}, and ${scope} were checked below.`,
  ]

  if (total === 0) {
    return {
      state: 'cannot_launch',
      headline: launchHeadline(rule),
      partial: false,
      known: [
        'These filters reach no contacts, so there is nothing to check yet.',
      ],
      unknown: rule.unknown,
      blockers: rule.blockers,
      betaReason: CAMPAIGN_BETA_REASON,
    }
  }

  if (rule.channel === 'none') {
    known.push(
      `This campaign type has no agreed contact requirement yet, so none of the ${assessed} checked were judged on their contact details.`,
    )
  } else {
    known.push(
      `${count(metrics.evidencePresent)} of the ${assessed} checked have ${rule.evidencePresentLabel.toLowerCase()}.`,
    )
    known.push(
      `${count(metrics.evidenceMissing)} of the ${assessed} checked have ${rule.evidenceMissingLabel.toLowerCase()}.`,
    )
  }

  if (metrics.clearanceBlocked > 0) {
    known.push(
      `${count(metrics.clearanceBlocked)} of the ${assessed} checked cannot be used for this campaign type at all.`,
    )
  }

  return {
    state: 'cannot_launch',
    headline: launchHeadline(rule),
    partial,
    known,
    unknown: rule.unknown,
    blockers: rule.blockers,
    betaReason: CAMPAIGN_BETA_REASON,
  }
}

export function normalizeCampaignSearch(value: string): string | undefined {
  const normalized = value.trim().replace(/\s+/g, ' ')
  return normalized || undefined
}

/**
 * Self-defending: it enforces both limits and drops empty terms itself rather
 * than trusting callers to have normalized first. An empty term would become
 * `column.ilike."%%"`, which matches every row — a filter that silently widens
 * to the whole accessible audience while the screen still calls it a search.
 */
export function campaignSearchTerms(value: string | undefined): string[] {
  if (!value) return []
  if (value.length > CAMPAIGN_SEARCH_CHARACTER_LIMIT) {
    throw new Error(
      `Campaign search supports at most ${CAMPAIGN_SEARCH_CHARACTER_LIMIT} characters`,
    )
  }
  const terms = value.split(/\s+/).filter(Boolean)
  if (terms.length > CAMPAIGN_SEARCH_TERM_LIMIT) {
    throw new Error(
      `Campaign search supports at most ${CAMPAIGN_SEARCH_TERM_LIMIT} terms`,
    )
  }
  return terms
}

/**
 * The gate in front of `campaignSearchTerms`, and it has to count terms exactly
 * the way that function does. Splitting on a literal space instead of `/\s+/`
 * put the two out of lockstep in both directions: a tab- or newline-separated
 * search of any length looked like a single term and passed the gate that
 * `campaignSearchTerms` would then throw on, and a search with repeated or edge
 * whitespace was counted by its gaps and refused although the enforcer would
 * have accepted it.
 *
 * That divergence is **not reachable from the shipped screen**, and this is not
 * a user-facing bug fix. `searchSchema` exposes no free-text URL parameter. The
 * four sites that call this validator all pass `normalizeCampaignSearch`
 * output, which has already collapsed runs of whitespace to single spaces and
 * trimmed the ends; the two that call `campaignSearchTerms` either normalize
 * inline or read state that was only ever set from normalized output. On
 * normalized input the two splitters always agreed.
 *
 * It is fixed because `useCampaignAudiencePreview` is an exported boundary that
 * takes `filters.search` from any future caller, and a gate that does not state
 * its enforcer's own precondition is a defect waiting for that caller.
 */
export function campaignSearchValidationError(
  value: string | undefined,
): string | null {
  if (!value) return null
  if (value.length > CAMPAIGN_SEARCH_CHARACTER_LIMIT) {
    return `Use no more than ${CAMPAIGN_SEARCH_CHARACTER_LIMIT} characters.`
  }
  if (value.split(/\s+/).filter(Boolean).length > CAMPAIGN_SEARCH_TERM_LIMIT) {
    return `Use no more than ${CAMPAIGN_SEARCH_TERM_LIMIT} search terms.`
  }
  return null
}

export function decodeCampaignAudiencePayload(
  data: unknown,
  count: number | null,
  maxRows: number,
): { rows: CampaignAudienceRow[]; total: number } {
  // A bounded page is fully determined: the server must return exactly
  // min(count, maxRows) rows. Anything else — short pages, over-long pages,
  // a missing count — is an incomplete response, not a smaller audience.
  if (
    count === null ||
    !Number.isSafeInteger(count) ||
    count < 0 ||
    !Number.isSafeInteger(maxRows) ||
    maxRows <= 0 ||
    !Array.isArray(data) ||
    data.length !== Math.min(count, maxRows)
  ) {
    throw new Error('Campaign audience response is incomplete')
  }

  const rows = data.map((value) => {
    if (!isRecord(value)) {
      throw new Error('Campaign audience row is malformed')
    }

    const account = value.account
    const phoneCounts = value.contact_phones
    if (
      typeof value.id !== 'string' ||
      typeof value.account_id !== 'string' ||
      typeof value.first_name !== 'string' ||
      typeof value.last_name !== 'string' ||
      (value.email !== null && typeof value.email !== 'string') ||
      typeof value.is_active !== 'boolean' ||
      !isAccount(account) ||
      !Array.isArray(phoneCounts) ||
      phoneCounts.length !== 1 ||
      !isRecord(phoneCounts[0]) ||
      !Number.isSafeInteger(phoneCounts[0].count) ||
      (phoneCounts[0].count as number) < 0
    ) {
      throw new Error('Campaign audience row is malformed')
    }

    return {
      id: value.id,
      account_id: value.account_id,
      first_name: value.first_name,
      last_name: value.last_name,
      email: value.email,
      is_active: value.is_active,
      // Reconstructed, not passed through: minimisation has to be uniform, or
      // a future projection widening carries extra account fields into app
      // state while every sibling field stays minimised.
      account: account === null ? null : { id: account.id, name: account.name },
      phone_count: phoneCounts[0].count as number,
    }
  })

  return { rows, total: count }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isAccount(
  value: unknown,
): value is CampaignAudienceRow['account'] {
  return (
    value === null ||
    (isRecord(value) &&
      typeof value.id === 'string' &&
      typeof value.name === 'string')
  )
}
